import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Mail, Users, LogOut, 
  TrendingUp, MessageSquare, Eye, Plus
} from 'lucide-react';
import { adminAuthAPI, adminAnalyticsAPI } from '../services/adminApi';
import { blogAPI, contactAPI, newsletterAPI } from '../services/api';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    verifyAuth();
    loadData();
  }, []);

  const verifyAuth = async () => {
    const isValid = await adminAuthAPI.verify();
    if (!isValid) {
      navigate('/admin/login');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsData, posts, contactsData, subscribersData] = await Promise.all([
        adminAnalyticsAPI.getAnalytics(),
        blogAPI.getPosts(),
        contactAPI.getSubmissions(),
        newsletterAPI.getSubscribers()
      ]);
      
      setAnalytics(analyticsData);
      setBlogPosts(posts);
      setContacts(contactsData);
      setSubscribers(subscribersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminAuthAPI.logout();
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D234E]"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-[#0D234E]">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">DMARKETINGCHAMP</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: LayoutDashboard },
              { id: 'blog', label: 'Blog Posts', icon: FileText },
              { id: 'contacts', label: 'Contacts', icon: MessageSquare },
              { id: 'subscribers', label: 'Subscribers', icon: Users }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#0D234E] border-b-2 border-[#0D234E]'
                    : 'text-gray-600 hover:text-[#0D234E]'
                }`}
              >
                <tab.icon size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Blog Posts</p>
                    <p className="text-3xl font-bold text-[#0D234E]">{analytics?.total_blog_posts || 0}</p>
                  </div>
                  <FileText size={40} className="text-[#BFB47E]" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Contact Submissions</p>
                    <p className="text-3xl font-bold text-[#0D234E]">{analytics?.total_contacts || 0}</p>
                  </div>
                  <MessageSquare size={40} className="text-[#BFB47E]" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Newsletter Subscribers</p>
                    <p className="text-3xl font-bold text-[#0D234E]">{analytics?.total_subscribers || 0}</p>
                  </div>
                  <Users size={40} className="text-[#BFB47E]" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Blog Categories</p>
                    <p className="text-3xl font-bold text-[#0D234E]">{analytics?.categories_count || 0}</p>
                  </div>
                  <TrendingUp size={40} className="text-[#BFB47E]" />
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold text-[#0D234E] mb-4">Recent Contacts</h3>
                <div className="space-y-3">
                  {contacts.slice(0, 5).map((contact) => (
                    <div key={contact.id} className="border-b border-gray-100 pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{contact.name}</p>
                          <p className="text-sm text-gray-600">{contact.email}</p>
                          <p className="text-xs text-gray-500 mt-1">{contact.service}</p>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(contact.submitted_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold text-[#0D234E] mb-4">Recent Subscribers</h3>
                <div className="space-y-3">
                  {subscribers.slice(0, 5).map((subscriber) => (
                    <div key={subscriber.id} className="border-b border-gray-100 pb-3">
                      <div className="flex justify-between items-center">
                        <p className="font-medium text-gray-900">{subscriber.email}</p>
                        <span className="text-xs text-gray-500">{formatDate(subscriber.subscribed_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Posts Tab */}
        {activeTab === 'blog' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0D234E]">Blog Posts</h2>
              <button className="flex items-center space-x-2 bg-[#0D234E] text-white px-4 py-2 rounded-md hover:bg-[#1a3a6e] transition-colors">
                <Plus size={20} />
                <span>New Post</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogPosts.map((post) => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{post.title}</td>
                      <td className="py-3 px-4">
                        <span className="bg-[#BFB47E] bg-opacity-20 text-[#0D234E] px-2 py-1 rounded-full text-sm">
                          {post.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(post.date)}</td>
                      <td className="py-3 px-4">
                        <button className="text-[#0D234E] hover:text-[#BFB47E] transition-colors">
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-[#0D234E] mb-6">Contact Submissions</h2>
            <div className="space-y-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">{contact.name}</h3>
                      <p className="text-sm text-gray-600">{contact.email}</p>
                      {contact.phone && <p className="text-sm text-gray-600">{contact.phone}</p>}
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(contact.submitted_at)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="bg-[#BFB47E] bg-opacity-20 text-[#0D234E] px-3 py-1 rounded-full text-sm">
                      {contact.service}
                    </span>
                  </div>
                  {contact.company && (
                    <p className="text-sm text-gray-600 mb-2">Company: {contact.company}</p>
                  )}
                  <p className="text-gray-700">{contact.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-[#0D234E] mb-6">Newsletter Subscribers</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Subscribed Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{subscriber.email}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(subscriber.subscribed_at)}</td>
                      <td className="py-3 px-4">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                          Active
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
